/* Photo clean-up for visiting cards (pure functions on pixel arrays - no OCR here).
   - flattens uneven light / shadow (difference from a blurred background),
   - crops to the area that actually has ink (card is often a small part of the photo),
   - produces two versions: dark-on-light as is, and a flipped one for light text on dark backgrounds. */

function toGray(data, n) {
  const g = new Float32Array(n);
  for (let i = 0, p = 0; i < n; i += 1, p += 4) g[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
  return g;
}

/** Smooth background estimate: block means, a few 3x3 blur passes, bilinear lookup. */
function background(gray, w, h, B) {
  const gw = Math.ceil(w / B);
  const gh = Math.ceil(h / B);
  let grid = new Float32Array(gw * gh);
  const cnt = new Float32Array(gw * gh);
  for (let y = 0; y < h; y += 1) {
    const gy = Math.min(gh - 1, Math.floor(y / B));
    for (let x = 0; x < w; x += 1) {
      const gi = gy * gw + Math.min(gw - 1, Math.floor(x / B));
      grid[gi] += gray[y * w + x];
      cnt[gi] += 1;
    }
  }
  for (let i = 0; i < grid.length; i += 1) grid[i] /= Math.max(cnt[i], 1);
  for (let pass = 0; pass < 3; pass += 1) {
    const next = new Float32Array(grid.length);
    for (let y = 0; y < gh; y += 1) {
      for (let x = 0; x < gw; x += 1) {
        let s = 0;
        let c = 0;
        for (let dy = -1; dy <= 1; dy += 1) {
          for (let dx = -1; dx <= 1; dx += 1) {
            const yy = y + dy;
            const xx = x + dx;
            if (yy >= 0 && yy < gh && xx >= 0 && xx < gw) { s += grid[yy * gw + xx]; c += 1; }
          }
        }
        next[y * gw + x] = s / c;
      }
    }
    grid = next;
  }
  const bg = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const fy = Math.min(gh - 1, Math.max(0, y / B - 0.5));
    const y0 = Math.floor(fy);
    const y1 = Math.min(gh - 1, y0 + 1);
    const ty = fy - y0;
    for (let x = 0; x < w; x += 1) {
      const fx = Math.min(gw - 1, Math.max(0, x / B - 0.5));
      const x0 = Math.floor(fx);
      const x1 = Math.min(gw - 1, x0 + 1);
      const tx = fx - x0;
      const a = grid[y0 * gw + x0] * (1 - tx) + grid[y0 * gw + x1] * tx;
      const b = grid[y1 * gw + x0] * (1 - tx) + grid[y1 * gw + x1] * tx;
      bg[y * w + x] = a * (1 - ty) + b * ty;
    }
  }
  return bg;
}

function percentileOf(hist, total, p) {
  const target = total * p;
  let acc = 0;
  for (let i = 0; i < 256; i += 1) {
    acc += hist[i];
    if (acc >= target) return i;
  }
  return 255;
}

/**
 * @param {Uint8ClampedArray} data RGBA pixels
 * @returns {{d: Float32Array, s: number, dark: number, box: {x0,y0,x1,y1}}}
 *   d = signed difference from the local background (ink darker = negative), s = typical ink strength.
 */
export function analyzePixels(data, w, h) {
  const n = w * h;
  const gray = toGray(data, n);
  const B = Math.max(12, Math.round(Math.max(w, h) / 90));
  const bg = background(gray, w, h, B);
  const d = new Float32Array(n);
  const hist = new Uint32Array(256);
  let darkArea = 0;
  for (let i = 0; i < n; i += 1) {
    d[i] = gray[i] - bg[i];
    hist[Math.min(255, Math.round(Math.abs(d[i])))] += 1;
    if (bg[i] < 105) darkArea += 1;
  }
  const s = Math.max(percentileOf(hist, n, 0.995), 22);
  const thr = 0.4 * s;
  const rows = new Uint32Array(h);
  const cols = new Uint32Array(w);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const v = d[y * w + x];
      if (Math.abs(v) > thr) { rows[y] += 1; cols[x] += 1; }
    }
  }
  const trim = (arr, len, minCount) => {
    let a = 0;
    let b = len - 1;
    while (a < len && arr[a] < minCount) a += 1;
    while (b > a && arr[b] < minCount) b -= 1;
    return [a, b];
  };
  const [y0, y1] = trim(rows, h, Math.max(2, Math.round(w * 0.004)));
  const [x0, x1] = trim(cols, w, Math.max(2, Math.round(h * 0.004)));
  return { d, s, dark: darkArea / n, box: { x0, y0, x1, y1 } };
}

/** Full-size grey bitmap (RGBA) from the signed difference. flip=true makes light-on-dark text dark.
 *  Soft mapping (no hard clipping of mid tones) so thin strokes and digits keep their shape. */
export function renderGray(d, s, flip, w, h) {
  const out = new Uint8ClampedArray(w * h * 4);
  const sign = flip ? -1 : 1;
  const k = 1 / (1.5 * s);
  for (let i = 0, o = 0; i < w * h; i += 1, o += 4) {
    const norm = Math.max(-1, Math.min(1, sign * d[i] * k));
    const v = Math.max(0, Math.min(255, 235 + 235 * norm));
    out[o] = v; out[o + 1] = v; out[o + 2] = v; out[o + 3] = 255;
  }
  return out;
}

/** Decide crop box (with margin) and output size. */
export function planCrop(info, w, h, target) {
  let { x0, y0, x1, y1 } = info.box;
  const mx = Math.round((x1 - x0) * 0.03) + 6;
  const my = Math.round((y1 - y0) * 0.03) + 6;
  x0 = Math.max(0, x0 - mx); y0 = Math.max(0, y0 - my); x1 = Math.min(w - 1, x1 + mx); y1 = Math.min(h - 1, y1 + my);
  const area = ((x1 - x0 + 1) * (y1 - y0 + 1)) / (w * h);
  if (area < 0.12 || x1 <= x0 || y1 <= y0) { x0 = 0; y0 = 0; x1 = w - 1; y1 = h - 1; }
  const cw = x1 - x0 + 1;
  const ch = y1 - y0 + 1;
  const scale = Math.max(0.5, Math.min(3, (target || 1800) / Math.max(cw, ch)));
  return { box: { x0, y0, x1, y1 }, outW: Math.round(cw * scale), outH: Math.round(ch * scale) };
}
